import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, CalendarPlus, ArrowRight, Sparkles, ShieldCheck, CalendarClock, RefreshCw, X, Video } from "lucide-react";
import { useUser } from "../../context/UserContext";
import { services } from "../../mock";
import { fetchMyBookings, cancelMyBooking, formatDate } from "../../lib/api";
import { toast } from "sonner";
import BookAppointmentModal from "./BookAppointmentModal";
import VideoMeetingModal from "../common/VideoMeetingModal";
import { AddToCalendar } from "../common/AddToCalendar";

const fmtDate = (iso) => formatDate(iso);

export default function PortalOverview() {
  const { user, token } = useUser();
  const firstName = user?.name?.split(" ")[0] || "there";
  const [bookOpen, setBookOpen] = useState(false);
  const [appts, setAppts] = useState([]);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [meeting, setMeeting] = useState(null);

  const refresh = () => {
    if (token) fetchMyBookings(token).then(setAppts).catch(() => {});
  };
  useEffect(() => { refresh(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBooked = async () => {
    if (rescheduleTarget) {
      try { await cancelMyBooking(token, rescheduleTarget); } catch (e) { /* ignore */ }
    }
    setRescheduleTarget(null);
    refresh();
  };

  const startReschedule = (id) => {
    setRescheduleTarget(id);
    setBookOpen(true);
  };

  const cancel = async (id) => {
    try {
      await cancelMyBooking(token, id);
      toast.success("Appointment cancelled");
      refresh();
    } catch (e) {
      toast.error("Could not cancel");
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-navy text-white p-7 sm:p-9">
        <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-gold/15 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-navy-light text-gold-light px-3 py-1 rounded-full text-xs font-medium">
            <Sparkles className="h-3.5 w-3.5" /> Member Portal
          </div>
          <h1 className="mt-3 font-serif text-2xl sm:text-3xl font-bold">
            Welcome, {firstName}!
          </h1>
          <p className="mt-2 text-gray-300 max-w-xl">
            This is your private space to message your coach, track your journey,
            and access your faith-based financial resources.
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link
          to="/portal/messages"
          className="group bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow"
        >
          <div className="h-12 w-12 rounded-lg bg-navy/5 flex items-center justify-center">
            <MessagesSquare className="h-6 w-6 text-navy" />
          </div>
          <h3 className="mt-4 font-serif text-lg font-semibold text-navy">
            Message Your Coach
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Ask questions and get personalized guidance from our back-office team.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-gold-dark font-semibold text-sm group-hover:gap-2.5 transition-all">
            Open messages <ArrowRight className="h-4 w-4" />
          </span>
        </Link>

        <button
          type="button"
          onClick={() => setBookOpen(true)}
          className="group bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          data-testid="book-consultation-button"
        >
          <div className="h-12 w-12 rounded-lg bg-gold/15 flex items-center justify-center">
            <CalendarPlus className="h-6 w-6 text-gold-dark" />
          </div>
          <h3 className="mt-4 font-serif text-lg font-semibold text-navy">
            Book a Consultation
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Schedule a free session to build your personalized financial roadmap.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-gold-dark font-semibold text-sm group-hover:gap-2.5 transition-all">
            Pick a time <ArrowRight className="h-4 w-4" />
          </span>
        </button>
      </div>

      <BookAppointmentModal open={bookOpen} onOpenChange={(o) => { setBookOpen(o); if (!o) setRescheduleTarget(null); }} onBooked={onBooked} />
      <VideoMeetingModal
        open={!!meeting}
        onOpenChange={(o) => !o && setMeeting(null)}
        url={meeting?.meetingUrl}
        title={meeting ? `${meeting.type} · ${fmtDate(meeting.date)} ${meeting.time}` : ""}
        name={user?.name}
      />

      {/* Upcoming appointments */}
      {appts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="upcoming-appointments">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="h-5 w-5 text-gold-dark" />
            <h3 className="font-serif text-lg font-semibold text-navy">Your Upcoming Appointments</h3>
          </div>
          <div className="space-y-3">
            {appts.slice(0, 4).map((a) => (
              <div key={a.id} className="flex items-center gap-4 bg-cream rounded-lg p-3.5" data-testid={`appt-${a.id}`}>
                <div className="text-center bg-navy text-white rounded-lg px-3 py-2 flex-shrink-0 w-16">
                  <div className="text-[10px] uppercase tracking-wide text-gold-light">{fmtDate(a.date).slice(0, 5)}</div>
                  <div className="text-lg font-bold leading-tight">{a.time}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-navy truncate">{a.type}</div>
                  <div className="text-xs text-gray-500">{fmtDate(a.date)} · <span className="capitalize">{a.status}</span></div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {a.meetingUrl && (
                    <button onClick={() => setMeeting(a)} title="Join video call" className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-navy hover:bg-navy-light px-2.5 py-1.5 rounded-md transition-colors" data-testid={`join-${a.id}`}>
                      <Video className="h-3.5 w-3.5" /> Join call
                    </button>
                  )}
                  <button onClick={() => startReschedule(a.id)} title="Reschedule" className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:text-gold-dark px-2 py-1.5 rounded-md hover:bg-white transition-colors" data-testid={`reschedule-${a.id}`}>
                    <RefreshCw className="h-3.5 w-3.5" /> Reschedule
                  </button>
                  <AddToCalendar appt={a} variant="link" />
                  <button onClick={() => cancel(a.id)} title="Cancel" className="text-gray-400 hover:text-red-500 p-1.5 rounded-md hover:bg-white transition-colors" data-testid={`cancel-${a.id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services available */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-navy">
            Services Available To You
          </h3>
          <ShieldCheck className="h-5 w-5 text-gold" />
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
          {services.slice(0, 6).map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-2.5 text-sm text-gray-700 bg-cream rounded-lg px-3 py-2.5"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {s.title}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
